import app from 'flarum/forum/app';
import { byId, load } from './cameraData';
import { cameraCard } from './components/CameraCard';

/**
 * Hover cards over every a.CameraLink in post bodies.
 *
 * Mirrors core's PostUser.js timing: 500 ms show timer, 250 ms hide timer,
 * visibility via an .in class that CSS transitions. The popover stays a DOM
 * descendant of the anchor, so pointer travel across the 8 px gap never fires
 * mouseleave and clearTimeout cancels any pending hide. If a future change
 * portals the popover to <body>, that analysis is void and the gap becomes a
 * dead zone (plan §06).
 *
 * Every popover element is a <span>: inside a post's <p>, div/ul/li unwind
 * the paragraph and the popover never renders.
 */

const SHOW_DELAY = 500;
const HIDE_DELAY = 250;

export function mountHoverCards() {
  let showTimer = null;
  let hideTimer = null;
  let current = null; // the anchor currently owning a popover

  function popoverFor(anchor) {
    const id = anchor.getAttribute('data-camera');
    const camera = byId[id];

    if (!camera) return null;

    const pop = document.createElement('span');
    pop.className = 'CameraPopover';
    // phrasing content ONLY -- see header comment
    m.render(pop, cameraCard(camera, { tag: 'span' }));
    return pop;
  }

  // The camera data is fetched, not bundled, so the first hover on a cold page
  // may have to wait for it. load() is already kicked off when the show timer
  // starts, and the timer is 500 ms, so by here it is normally resolved --
  // but the pointer can still have left in the meantime, hence the :hover
  // re-check. Every later hover takes the resolved-promise fast path.
  function show(anchor) {
    load().then(() => {
      if (!anchor.matches(':hover')) return;

      showResolved(anchor);
    });
  }

  function showResolved(anchor) {
    hidePopover();

    const pop = popoverFor(anchor);
    if (!pop) return;

    anchor.appendChild(pop);
    current = { anchor, pop };

    // Position after insert so offsetHeight is real. The popover is
    // position:fixed because core's .Post-body sets overflow:auto and clipped
    // an absolutely positioned card at the post edge. It stays a DOM child of
    // the anchor, so the pointer-gap analysis in the header comment holds --
    // only the painting escapes the post.
    const rect = anchor.getBoundingClientRect();
    const above = window.innerHeight - rect.bottom < pop.offsetHeight + 16;

    if (above) pop.classList.add('CameraPopover--above');

    pop.style.top = (above ? rect.top - pop.offsetHeight - 8 : rect.bottom + 8) + 'px';
    pop.style.left = Math.max(8, Math.min(rect.left, window.innerWidth - pop.offsetWidth - 8)) + 'px';

    requestAnimationFrame(() => pop && pop.classList.add('in'));
  }

  function hidePopover() {
    if (!current) return;

    const { pop } = current;
    pop.classList.remove('in');

    const gone = () => pop.parentNode && pop.parentNode.removeChild(pop);
    const wait = parseFloat(getComputedStyle(pop).transitionDuration || '0') * 1000 || 0;
    setTimeout(gone, wait);
    current = null;
  }

  function target(e) {
    return e.target instanceof Element ? e.target.closest('a.CameraLink') : null;
  }

  // ponytail: a fixed popover does not follow the page, so drop it on scroll
  // rather than repositioning it every frame. Capture, because the scroller is
  // .Post-body / the window depending on where the link sits.
  window.addEventListener('scroll', () => current && hidePopover(), { passive: true, capture: true });

  document.addEventListener('mouseover', (e) => {
    const anchor = target(e);

    if (anchor) {
      // re-entering the anchor or moving into its popover cancels any hide
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (!current || current.anchor !== anchor) {
        load(); // start the fetch now so the 500 ms delay covers it
        if (showTimer) clearTimeout(showTimer);
        showTimer = setTimeout(() => {
          showTimer = null;
          show(anchor);
        }, SHOW_DELAY);
      }
    } else if (current || showTimer) {
      // left the anchor (and its popover, which lives inside it)
      if (showTimer) {
        clearTimeout(showTimer);
        showTimer = null;
      }
      if (current) {
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          hideTimer = null;
          hidePopover();
        }, HIDE_DELAY);
      }
    }
  });
}
